import { FileCompressService } from '../../files-helpers/file-compress.service';
import { FileConvertService } from '../../files-helpers/file-convert.service';
import { map, switchMap } from 'rxjs/operators';

import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UserFile } from '@nrwl/rx-models';
import { environment } from '@nrwl/environment';

@Injectable({
  providedIn: 'root',
})
export class UserFileService {
  constructor(
    private http: HttpClient,
    private fileConvert: FileConvertService,
    private fileCompress: FileCompressService,
  ) {}

  // GET
  public get(id): Observable<UserFile> {
    return this.http
      .get(`${environment.baseAPIUrlv3}user-file/${id}/`)
      .pipe(map((resp: UserFile | any) => resp));
  }

  // GET Bulk Files
  public getAll(ids: string): Observable<[UserFile]> {
    return this.http.get(`${environment.baseAPIUrlv3}user-file/bulk/?ids=${ids}`).pipe(
      map((resp: [UserFile] | any) => {
        const result = <any>[];
        ids.split(',').map((item) => {
          const temp: any = resp.find((i) => i.id === +item);
          if (temp) {
            result.push(temp);
          }
        });
        return <[UserFile]>result;
      }),
    );
  }

  // POST
  public post(data): Observable<UserFile> {
    return this.http
      .post(`${environment.baseAPIUrlv3}user-file/`, data)
      .pipe(map((resp: UserFile | any) => resp));
  }

  public deleteFile(fileId: number): Observable<any> {
    return this.http.delete(`${environment.baseAPIUrlv3}user-file/${fileId}`);
  }

  public rasterize(formData): Observable<[UserFile]> {
    return this.http
      .post(`${environment.baseAPIUrlv3}user-file/rasterize/`, formData)
      .pipe(map((resp: [UserFile] | any) => resp));
  }

  public postVideo(video: any, thumb: any = null) {
    const formData: FormData = new FormData();
    formData.append('file', video, video.name);
    if (thumb) {
      // WARNING: MUST HAVE: 2020/02/24 back-end does not support file upload for thumb field. Negotiating
      formData.append('thumb', thumb, 'video-thumb.png');
    }
    return this.post(formData);
  }

  public postBase64(b64String: string) {
    const formData = this.fileConvert.b64toFormData(b64String);
    return this.post(formData);
  }

  public postBase64WithCompression(fileStr: string) {
    const file = this.fileConvert.b64toFile2(fileStr);
    return this.fileCompress.compress(file).pipe(
      switchMap((compressedFormData: FormData) => {
        return this.post(compressedFormData);
      }),
    );
  }

  public toBase64viaCanvas(url, callback, callbackError) {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = <any>document.createElement('CANVAS');
      const ctx = canvas.getContext('2d');
      canvas.height = img.height;
      canvas.width = img.width;
      ctx.drawImage(img, 0, 0);
      callback(canvas.toDataURL());
    };
    img.onerror = (err) => {
      callbackError(err);
    };

    img.src = url;
  }

  public postBase64ProgressBar(b64String: string) {
    const formData = this.b64toFormData(b64String);
    return this.postFileProgressBar(formData);
  }

  public b64toFormData(b64String: string, fieldName = 'file'): FormData {
    const contentType = this.getBase64ContentType(b64String);
    const realData = this.getBase64Data(b64String);
    const blob = this.b64toBlob(realData, contentType);
    const file = new File([blob], 'provider-image.png');

    const formData = new FormData();
    formData.append(fieldName, file);
    return formData;
  }

  public postFileProgressBar(data): Observable<HttpEvent<any>> {
    return this.http.post(`${environment.baseAPIUrlv3}user-file/`, data, {
      reportProgress: true,
      observe: 'events',
    });
  }

  public patchUserFile(fileId, data): Observable<any> {
    return this.http.patch(`${environment.baseAPIUrlv3}user-file/${fileId}/`, data);
  }

  public getBase64ContentType(b64Data) {
    const block = b64Data.split(';');
    return block[0].split(':')[1];
  }

  public getBase64Data(b64Data) {
    const block = b64Data.split(';');
    return block[1].split(',')[1];
  }

  // base64 to blob helper
  public b64toBlob(b64Data, contentType, sliceSize?) {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;

    let byteCharacters = b64Data;
    try {
      byteCharacters = atob(b64Data);
    } catch (e) {
      console.log('SKIPPING: Failure to decode base64 string');
    }

    const byteArrays = <any>[];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray: any = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  public b64toFile(b64String: string, fieldName = 'file'): File {
    const contentType = this.getBase64ContentType(b64String);
    const realData = this.getBase64Data(b64String);
    const blob = this.b64toBlob(realData, contentType);
    const file = new File([blob], 'provider-image.png');
    return file;
  }

  public postVideoProgressBar(video: any, thumb: any = null) {
    const formData: FormData = new FormData();
    formData.append('file', video, video.name);
    if (thumb) {
      // WARNING: MUST HAVE: 2020/02/24 back-end does not support file upload for thumb field. Negotiating
      formData.append('thumb', thumb, 'video-thumb.png');
    }
    return this.postFileProgressBar(formData);
  }
}
